import { Link, Body } from '@panwds/react-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslate } from "../../customHooks";
import { isEmpty } from 'lodash';
import CircularLoader from '../../components/CircularLoader/CircularLoader';
import { FormattedMessage } from 'react-intl';
import { useEffect } from 'react';
import { useSubscriptionParams } from './hooks/useSubscriptionParams';
import { useValidateBillingCode } from './hooks/useValidateBillingCode';
import { useSubscriptionCreate } from './hooks/useSubscriptionCreate';
import { RouteUri } from '../../routeUri';
import {
    SubscriptionAccountIdField,
    SubscriptionEmailField,
    HasBillingCodeRadioGroup
} from './components/FormFields';
import { BillingCodeFieldGroup, TrialUserFieldGroup } from './components/ConditionalFieldGroups';
import { SubscriptionButton, SubscriptionSubmitButton } from './components/ButtonComponents';
import { FormLayout } from '@panwds/react-form';
import {
    SUBSCRIPTION_FIELDS,
    SUBSCRIPTION_I18N_KEYS,
    SUBSCRIPTION_METRICS,
    SUBSCRIPTION_URLS
} from './constants';

export const SubscriptionCreate = (props: any) => {
    const translate = useTranslate();
    const history = useHistory();
    const location = useLocation();
    const { accountId } = useSubscriptionParams();
    const { isValidating: validatingScmCode } = useValidateBillingCode();
    const { handleSubmit, isLoading, nextStep } = useSubscriptionCreate();

    const formMethods = useForm({});
    const {
        formState: { dirtyFields }, watch
    } = formMethods;

    const hasCode = watch(SUBSCRIPTION_FIELDS.HAS_BILLING_ASSOCIATION_CODE);

    useEffect(() => {
            if (props.setHasCode && hasCode) {
                props.setHasCode(hasCode);
            }
        }, [hasCode, props.setHasCode]);

    // Wrapper to pass hasCode as separate parameter
    const onSubmit = (formData: any) => {
        handleSubmit(formData, hasCode);
    };

    return (
        <>
            <Body size="lg" addClassName="tw-text-center tw-py-4 tw-font-semibold" style={{ fontSize: '20px' }}>{translate(SUBSCRIPTION_I18N_KEYS.TITLE)}</Body>
            {nextStep && nextStep.UserName ? (
                <Body as="div" addClassName="tw-px-4 tw-py-2 tw-space-y-4">
                    <Body size="md" style={{ fontSize: '16px' }}>
                        <FormattedMessage
                            id={SUBSCRIPTION_I18N_KEYS.ACTIVATE_MESSAGE}
                            values={{
                                break: <br />,
                                link: <Link
                                    dataMetrics={SUBSCRIPTION_METRICS.GET_LOGIN_HELP}
                                    dataTestId={SUBSCRIPTION_METRICS.GET_LOGIN_HELP}
                                    external
                                    href={SUBSCRIPTION_URLS.LOGIN_ASSISTANCE}
                                >
                                    {translate(SUBSCRIPTION_I18N_KEYS.ACTIVATE_GET_HELP)}
                                </Link>
                            }} />
                    </Body>
                    <SubscriptionButton
                        dataMetrics={SUBSCRIPTION_METRICS.SIGN_UP_LINK_NEW_TENANT}
                        type="submit"
                        onClick={() => {
                            // Keep the marketplace params and add the BAC from the form, so it reaches the
                            // link-account step on both the Okta and Cognito paths. The email goes in router
                            // state (not the URL) so Login can pre-fill it.
                            const query = new URLSearchParams(location.search);
                            if (nextStep.BillingAssociationCode) {
                                query.set('BillingAssociationCode', nextStep.BillingAssociationCode);
                            }
                            history.push({
                                pathname: RouteUri.Login,
                                search: `?${query.toString()}`,
                                state: { loginHint: nextStep.UserName },
                            });
                        }}
                    >
                        {translate(SUBSCRIPTION_I18N_KEYS.ACTIVATE_LOGIN)}
                    </SubscriptionButton>
                </Body>
            ) :
                (<FormProvider {...formMethods}>
                    <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                        <Body as="div" addClassName={`tw-relative ${isLoading ? "loadingBlur" : ""}`}>
                            {(isLoading || validatingScmCode) && <CircularLoader loading={true} />}
                            <FormLayout>
                                <SubscriptionAccountIdField value={accountId} />

                                <HasBillingCodeRadioGroup />

                                <SubscriptionEmailField />

                                {hasCode === "Yes" && <BillingCodeFieldGroup />}
                                {hasCode === "No" && <TrialUserFieldGroup />}

                                <SubscriptionSubmitButton
                                    dataMetrics={SUBSCRIPTION_METRICS.SIGN_UP_SAVE}
                                    disabled={isEmpty(dirtyFields)}
                                >
                                    {translate(SUBSCRIPTION_I18N_KEYS.SAVE)}
                                </SubscriptionSubmitButton>
                            </FormLayout>
                        </Body>
                    </form>
                </FormProvider>)
            }
        </>
    );
};
